import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { type ReactNode, useEffect, useMemo, useState } from 'react'

import { getHighestRole } from '@ankor-io/common/domain/iam/account'
import { AccountPreferenceKeys, type AccountPreferences, Role } from '@ankor-io/common/types/account'

import { useGetMeQuery } from '@/api/queries/useUsersQuery'
import { isGTMReady, setUsername } from '@/utils/gtm'

import { UserContext } from './UserContext'
import type { UserContextType } from './types'

export function UserContextProvider({ children }: { children: ReactNode }) {
  // get kindeUser details
  const { user: kindeUser, isAuthenticated, getAccessToken } = useKindeAuth()

  const [user, setUser] = useState<UserContextType | null>(null)
  const [activeOrganizationId, setActiveOrganizationId] = useState<string | null>(null)
  const [activeVesselId, setActiveVesselId] = useState<string | null>(null)
  const [activeConnectedAccountId, setActiveConnectedAccountId] = useState<string | null>(null)

  // Use React Query to fetch user data
  const {
    data: accountData,
    isLoading,
    error,
  } = useGetMeQuery({
    enabled: isAuthenticated && !!kindeUser,
  })

  const activeOrganizationRole: Role = useMemo(() => {
    return activeOrganizationId && user?.organizations[activeOrganizationId]
      ? getHighestRole(user?.organizations[activeOrganizationId].roles) || Role.VIEWER
      : Role.VIEWER
  }, [user, activeOrganizationId])

  useEffect(() => {
    if (!isAuthenticated) {
      setUser(null)
      setActiveOrganizationId(null)
      setActiveVesselId(null)
      setActiveConnectedAccountId(null)
      return
    }

    if (!kindeUser) {
      return
    }

    if (error) {
      console.error('Failed to fetch account data', error)
      // Set user with basic kindeUser data even on error
      const userData = {
        id: kindeUser.id,
        givenName: kindeUser?.givenName || '',
        familyName: kindeUser?.familyName || '',
        email: kindeUser?.email || '',
        organizations: {},
        preferences: {} as AccountPreferences,
        permissions: [],
      }
      setUser(userData)
      return
    }

    if (accountData && kindeUser) {
      const userData = {
        id: kindeUser.id,
        givenName: kindeUser?.givenName || '',
        familyName: kindeUser?.familyName || '',
        email: kindeUser?.email || '',
        organizations: accountData.organizations,
        preferences: accountData.preferences || {},
        permissions: accountData.permissions || [],
      }

      setUser(userData)

      // Prefer saved active org only when the user still belongs to it (e.g. after leaving an org).
      // Otherwise fall back to the first remaining organization, or null.
      const organizationKeys = Object.keys(accountData.organizations)
      const preferredOrgId = userData.preferences?.[AccountPreferenceKeys.ACTIVE_ORGANIZATION_ID]
      const firstOrgId =
        (preferredOrgId && organizationKeys.includes(preferredOrgId) ? preferredOrgId : undefined) ??
        (organizationKeys.length > 0 ? organizationKeys[0] : null)
      setActiveOrganizationId(firstOrgId)

      // If the user has an active vessel inside preference, use it
      const activeVesselId = userData.preferences?.[AccountPreferenceKeys.ACTIVE_VESSEL_ID] || null
      setActiveVesselId(activeVesselId)

      // If the user has an active connected account inside preference, use it
      const activeConnectedAccountId = userData.preferences?.[AccountPreferenceKeys.ACTIVE_CONNECTED_ACCOUNT_ID] || null
      setActiveConnectedAccountId(activeConnectedAccountId)

      // Set GTM username (only if GTM is initialized to avoid console warnings)
      if (isGTMReady()) {
        setUsername(userData.id)
      }
    }
  }, [accountData, kindeUser, isAuthenticated, error])

  const updateOrganization = (orgId: string, name: string, description: string) => {
    if (!user || !user.organizations[orgId]) {
      return
    }

    const updatedUser = { ...user }
    const updatedOrganization = { ...updatedUser.organizations[orgId], name, description }
    updatedUser.organizations[orgId] = updatedOrganization

    setUser(updatedUser)

    // Save the active organization id to the preferences
    // Skip if this org is already the active one (avoids redundant updates during onboarding)
    if (activeOrganizationId !== orgId) {
      updatePreferences({ activeOrganizationId: orgId })
    }
  }

  const updatePreferences = async (updatedPreferences: AccountPreferences) => {
    const res = await fetch('/api/iam/me/preferences', {
      method: 'PUT',
      body: JSON.stringify({ preferences: updatedPreferences }),
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
      },
    })

    if (res.ok) {
      // The type error occurs because spreading user creates a new object that loses the type information
      // We need to explicitly cast it back to UserContextType to maintain the type safety
      const updatedUser: UserContextType = {
        ...user!,
        preferences: { ...user!.preferences, ...updatedPreferences },
      }

      setUser(updatedUser)
    }
  }

  const updateActiveOrganizationId = (orgId: string) => {
    if (activeOrganizationId === orgId) {
      return
    }

    // When we update the activeOrganizationId, we need to reset vessel and connected account as well.
    // Vessels are org-scoped, so switching org clears activeVesselId and activeConnectedAccountId.
    setActiveOrganizationId(orgId)
    setActiveVesselId(null)
    setActiveConnectedAccountId(null)
    updatePreferences({ activeOrganizationId: orgId, activeVesselId: '', activeConnectedAccountId: '' })
  }

  const updateActiveVesselId = (vesselId: string) => {
    if (activeVesselId === vesselId) {
      return
    }

    setActiveVesselId(vesselId)
    updatePreferences({ activeVesselId: vesselId })
  }

  const updateActiveConnectedAccountId = (connectedAccountId: string, skipPreferencesUpdate = false) => {
    if (activeConnectedAccountId === connectedAccountId) {
      return
    }

    setActiveConnectedAccountId(connectedAccountId)
    // Skip preference update if caller will handle it (during onboarding)
    if (!skipPreferencesUpdate) {
      updatePreferences({ activeConnectedAccountId: connectedAccountId })
    }
  }

  return (
    <UserContext.Provider
      value={{
        user,
        isLoading,
        organizations: user?.organizations || {},
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
        activeOrganizationRole,
        preferences: user?.preferences || {},
        updateOrganization,
        updateActiveOrganizationId,
        updateActiveVesselId,
        updateActiveConnectedAccountId,
        updatePreferences,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}
